import { useState, type ReactNode } from 'react'

import { Sheet, SheetContent, SheetTrigger } from '@/shared/components/ui'
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover'
import { useIsMobile } from '@/shared/hooks'
import { cn } from '@/shared/lib'

export interface SelectOption<TId extends string> {
  id: TId
  label: string
}

interface SelectPopoverProps<TId extends string> {
  title: string
  options: ReadonlyArray<SelectOption<TId>>
  onSelect: (optionId: TId) => void
  triggerButton: ReactNode
  className?: string
  width?: string
  showIndicator?: boolean
}

export default function SelectPopover<TId extends string>({
  title,
  options,
  onSelect,
  triggerButton,
  className,
}: Readonly<SelectPopoverProps<TId>>) {
  const [open, setOpen] = useState(false)

  const handleSelect = (optionId: TId) => {
    onSelect(optionId)
    setOpen(false)
  }
  const isMobile = useIsMobile()
  const Container = isMobile ? Sheet : Popover
  const Trigger = isMobile ? SheetTrigger : PopoverTrigger
  const Content = isMobile ? SheetContent : PopoverContent

  return (
    <div className="relative">
      <Container open={open} onOpenChange={setOpen}>
        <Trigger asChild>{triggerButton}</Trigger>
        <div
          className={`${open ? 'inset-0 z-60 bg-black/30 md:fixed' : 'hidden'}`}
          data-slot="popover-overlay"
          data-testid="popover-overlay"
        >
          <Content
            className={cn(
              'md:width-auto rounded-t-xl md:relative md:z-100 md:mt-2 md:rounded-lg md:p-0 md:shadow-md',
              'max-h-[80vh] md:max-h-[400px]',
              'overflow-auto',
              className,
            )}
            align="center"
            sideOffset={5}
            side="bottom"
            closeClassname="top-4 right-4"
          >
            <div className="font-poppins relative z-20 mt-14 px-6 py-2 text-left font-medium md:mt-0 md:px-3">
              {title}
            </div>
            <div className="relative z-20 flex flex-col">
              {options.map((option) => (
                <div
                  role="none"
                  key={option.id}
                  className={cn(
                    'font-nunito flex h-14 cursor-pointer items-center px-6 text-sm font-medium md:h-11 md:font-normal',
                    'hover:bg-gray-100',
                  )}
                  onClick={() => handleSelect(option.id)}
                >
                  {option.label}
                </div>
              ))}
            </div>
          </Content>
        </div>
      </Container>
    </div>
  )
}
